<template>
  <div class="human-clone-page">
    <div class="settings-card">
      <h2 class="page-title">
        <Icon icon="carbon:ai" width="24" height="24" />
        AI 数字人形象
      </h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <div class="search-left">
          <el-input v-model="keyword" placeholder="搜索形象名称或场景名称" clearable size="large" class="search-input"
            @keyup.enter="onSearch">
            <template #prefix>
              <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            </template>
          </el-input>

          <el-button type="primary" size="large" @click="onSearch" class="search-btn">
            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            <span class="btn-text">搜索</span>
          </el-button>

          <el-button size="large" @click="resetSearch" plain>
            <Icon icon="heroicons:arrow-path" width="18" height="18" />
            <span class="btn-text">清空</span>
          </el-button>

          <el-popconfirm :title="`确认删除选中的 ${selectedIdsArr.length} 个形象吗？`" v-if="selectedIdsArr.length"
            @confirm="doBatchDelete">
            <template #reference>
              <el-button type="danger" :disabled="!selectedIdsArr.length" size="large">
                <Icon icon="heroicons:trash" width="18" height="18" />
                <span class="btn-text">批量删除（{{ selectedIdsArr.length }}）</span>
              </el-button>
            </template>
          </el-popconfirm>

          <el-button @click="toggleSelectAll" :disabled="!list.length" plain size="large">
            <Icon :icon="isAllSelected ? 'heroicons:check-circle' : 'heroicons:circle'" width="18" height="18" />
            <span class="btn-text">{{ isAllSelected ? '取消全选' : '全选当前页' }}</span>
          </el-button>
        </div>

        <el-button type="success" size="large" @click="openCreate" class="add-btn">
          <Icon icon="heroicons:plus" width="18" height="18" />
          <span class="btn-text">新建形象</span>
        </el-button>
      </div>

      <!-- 卡片网格 -->
      <div class="cards-wrapper" v-loading="loading">
        <el-empty description="暂无形象，点击右上角创建第一个AI数字人形象" v-if="!list.length && !loading" class="empty-state" />

        <div class="cards-grid">
          <div class="data-card" v-for="item in list" :key="item.id">
            <!-- 选择框 -->
            <div class="card-checkbox">
              <el-checkbox :model-value="selectedIds.has(item.id)" @change="toggleOne(item.id)" />
            </div>

            <!-- 媒体区域 -->
            <div class="card-media">
              <div class="video-container">
                <video v-if="item.human_video_url" :src="item.human_video_url" preload="metadata" playsinline
                  @loadedmetadata="onVideoMeta(item, $event)" :class="item._videoClass || ''" />
                <div class="video-placeholder" v-else>
                  <Icon icon="heroicons:video-camera" width="48" height="48" />
                </div>
              </div>

              <div class="card-overlay">
                <div class="action-buttons">
                  <button class="action-btn primary-btn" @click="goCreate(item)">
                    <Icon icon="heroicons:sparkles" width="14" height="14" />
                    <span>立即创作</span>
                  </button>
                  <el-popconfirm title="确认删除该形象？" @confirm="confirmDelete(item.id)">
                    <template #reference>
                      <button class="action-btn danger-btn">
                        <Icon icon="heroicons:trash" width="14" height="14" />
                      </button>
                    </template>
                  </el-popconfirm>
                </div>
              </div>

              <div class="status-badge" :class="'status-' + item.status">
                {{ statusName(item.status) }}
              </div>
            </div>

            <!-- 内容区域 -->
            <div class="card-content">
              <div class="card-title-row">
                <h3 class="card-title" :title="item.human_name">{{ item.human_name }}</h3>
                <span class="clone-tag">普通克隆</span>
              </div>

              <div class="card-info">
                <div class="info-row">
                  <Icon icon="heroicons:map-pin" width="14" height="14" />
                  <span class="scene-name">{{ item.human_scene }}</span>
                </div>
                <div class="info-row">
                  <Icon icon="heroicons:document-text" width="14" height="14" />
                  <span class="scene-id">场景ID: {{ item.scene_task_id || '—' }}</span>
                </div>
              </div>

              <div class="card-error" v-if="item.error_message" :title="item.error_message">
                <Icon icon="heroicons:exclamation-triangle" width="14" height="14" />
                <span>{{ item.error_message }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container" v-if="total > 0">
        <el-pagination v-model:current-page="page" v-model:page-size="pageSize" :total="total"
          :page-sizes="pageSizes" :layout="paginationLayout" @size-change="handleSizeChange"
          @current-change="handlePageChange" background />
      </div>
    </div>

    <!-- 协议弹窗 -->
    <el-dialog v-model="agreementDialogVisible" :title="agreementTitle || '克隆使用协议'" :width="dialogWidth"
      :close-on-click-modal="false" :close-on-press-escape="false" destroy-on-close class="custom-dialog">
      <div v-loading="agreementLoading" class="agreement-content">
        <div class="agreement-meta" v-if="agreementUpdatedAt">
          最近更新：{{ agreementUpdatedAt }}
        </div>
        <pre class="agreement-pre" v-if="agreementContent">{{ agreementContent }}</pre>
        <el-empty v-else description="未读取到协议内容" />
      </div>
      <template #footer>
        <button class="dialog-btn cancel-btn" @click="agreementDialogVisible = false"
          :disabled="agreeCountdown > 0">
          关闭{{ agreeCountdown > 0 ? `（${agreeCountdown}s）` : '' }}
        </button>
        <button class="dialog-btn confirm-btn" :disabled="agreeCountdown > 0" @click="confirmAgree">
          {{ agreeCountdown > 0 ? `请阅读（${agreeCountdown}s）` : '我已阅读并同意' }}
        </button>
      </template>
    </el-dialog>

    <!-- 新建形象抽屉 -->
    <el-drawer v-model="drawerVisible" :with-header="true" size="860px" :close-on-click-modal="false" destroy-on-close
      title="AI 数字人形象克隆" class="custom-drawer">
      <div class="drawer-content">
        <!-- 表单区域 -->
        <div class="form-section">
          <el-form ref="formRef" :model="form" :rules="rules" :label-width="formLabelWidth" class="clone-form">
            <el-form-item label="形象名称" prop="human_name">
              <el-input v-model="form.human_name" placeholder="支持多个；用中文、英文逗号分隔" size="large">
                <template #append>
                  <button class="input-append-btn" @click="insertSep('human_name')" type="button">
                    <Icon icon="heroicons:plus" width="14" height="14" />
                    插入分隔符
                  </button>
                </template>
              </el-input>
            </el-form-item>

            <el-form-item label="克隆模式">
              <span class="mode-tag">普通克隆</span>
            </el-form-item>

            <el-form-item label="场景名称" prop="human_scene">
              <el-input v-model="form.human_scene" placeholder="支持多个；用中文、英文逗号分隔" size="large">
                <template #append>
                  <button class="input-append-btn" @click="insertSep('human_scene')" type="button">
                    <Icon icon="heroicons:plus" width="14" height="14" />
                    插入分隔符
                  </button>
                </template>
              </el-input>
            </el-form-item>

            <el-form-item label="视频素材" prop="human_video_url" class="video-url-item">
              <div class="uploader-panel">
                <div class="uploader-header">
                  <el-input v-model="form.human_video_url" type="textarea" :rows="2" class="mono-input"
                    placeholder="粘贴视频直链；支持多个，用英文逗号 , 分隔" />
                  <div class="uploader-hint">
                    <div class="uploader-price" v-if="!configLoading">
                      单价：<b>{{ cloneUnitPrice }}</b> 点 / 每个视频
                    </div>
                    <Icon icon="heroicons:document-text" width="14" height="14" />
                    示例：https://your-bucket.oss-xx.aliyuncs.com/path/video.mp4
                  </div>
                  <div v-if="uploadedUrls.length" class="url-chips">
                    <span v-for="(u, i) in uploadedUrls" :key="u" class="url-chip">
                      {{ truncateUrl(u) }}
                      <button class="chip-close" @click="removeUrl(i)">
                        <Icon icon="heroicons:x-mark" width="12" height="12" />
                      </button>
                    </span>
                  </div>
                </div>

                <div class="uploader-body">
                  <el-upload class="drop-card" drag multiple :auto-upload="false" :show-file-list="false"
                    accept=".mp4,.mov,.m4v,.webm,.flv,.f4v,.mkv,.rm,.rmvb,.3gp,.3g2,.wmv,.avi,.asf,.mpg,.mpeg,.ts"
                    :on-change="onPickLocalVideo">
                    <div class="drop-inner">
                      <div class="drop-icon">
                        <Icon icon="heroicons:cloud-arrow-up" width="32" height="32" />
                      </div>
                      <div class="drop-title">将视频拖到此处</div>
                      <div class="drop-sub">或 <span class="link">点击选择</span>（可多选）</div>
                      <div class="drop-meta">系统会自动校验 <b>5~120 秒</b>，并上传到 OSS 填入直链</div>
                    </div>
                  </el-upload>

                  <div class="uploader-aside">
                    <div class="aside-title">
                      <Icon icon="heroicons:information-circle" width="16" height="16" />
                      上传说明
                    </div>
                    <ul class="aside-list">
                      <li>建议分辨率 ≥ 720p，码率≥4Mbps</li>
                      <li>支持常见容器：mp4</li>
                      <li>每个素材建议 10~60 秒更稳</li>
                      <li>可一次选择多个文件批量上传</li>
                    </ul>

                    <div v-if="queue.length" class="queue">
                      <div class="queue-title">上传进度</div>
                      <div v-for="q in queue" :key="q.id" class="queue-item">
                        <span class="q-name" :title="q.name">{{ q.name }}</span>
                        <el-progress :percentage="q.percent" :status="q.status" :stroke-width="6" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </el-form-item>

            <el-form-item class="agreement-item">
              <label class="checkbox-label">
                <input type="checkbox" v-model="agree" @change="onAgreeBoxChange" />
                <span>我已阅读并同意</span>
                <button class="link-btn" type="button" @click.prevent="openAgreement(false)">
                  《克隆使用协议》
                </button>
              </label>
            </el-form-item>

            <el-form-item class="form-actions">
              <button class="dialog-btn confirm-btn submit-btn" :disabled="submitting || !agree" @click="submit">
                <Icon icon="heroicons:sparkles" width="16" height="16" />
                <span>克隆形象（{{ costDisplayText }}）</span>
              </button>
              <button class="dialog-btn cancel-btn" @click="drawerVisible = false">
                取消
              </button>
            </el-form-item>
          </el-form>
        </div>

        <!-- 示例与规范区域 -->
        <div class="guide-section">
          <div class="guide-grid">
            <div class="sample-card">
              <div class="sample-header">
                <span class="sample-title">示例视频</span>
                <el-tooltip content="参考此视频格式和内容进行录制" placement="top">
                  <Icon icon="heroicons:question-mark-circle" width="16" height="16" />
                </el-tooltip>
              </div>
              <div class="sample-body">
                <video class="sample-video" src="https://api.niwaai.com/video_exp.mp4" controls playsinline
                  preload="metadata" />
              </div>
            </div>

            <div class="tips-container">
              <div class="tips-group ok-tips">
                <div class="tips-header">
                  <Icon icon="heroicons:check-circle" width="18" height="18" class="tips-icon" />
                  <span class="tips-title">建议</span>
                </div>
                <ul class="tips-list">
                  <li>提交 <b>5~120秒</b> 的原视频素材</li>
                  <li>人脸占比不超过画面 <b>1/2</b></li>
                  <li>可静坐、走路、骑车，实景/绿幕均可</li>
                  <li>尽量高清且光线明亮均匀</li>
                  <li>每帧都要露脸，侧脸幅度不要过大</li>
                  <li>句与句之间自然停顿，闭口</li>
                  <li>手势自然，不要遮挡嘴巴</li>
                </ul>
              </div>

              <div class="tips-group bad-tips">
                <div class="tips-header">
                  <Icon icon="heroicons:x-circle" width="18" height="18" class="tips-icon" />
                  <span class="tips-title">忌讳</span>
                </div>
                <ul class="tips-list">
                  <li>素材过长导致失败或极慢</li>
                  <li>人脸占比过大/过小</li>
                  <li>嘴巴与面部 3/4 以上在镜头内</li>
                  <li>明显阴影或过曝/模糊</li>
                  <li>频繁转移视线、频繁环顾</li>
                  <li>说话无停顿，假笑一直露齿</li>
                  <li>动作过于频繁或镜头过抖</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config.js'
import { Icon } from '@iconify/vue'
import { ElMessage } from 'element-plus'
import { getUploadSignature, uploadToOSS, beforeUpload, getVideoDuration } from '@/utils/uploadUtils'

export default {
  name: 'HumanClone',
  components: { Icon },
  data() {
    return {
      loading: false,
      list: [],
      drawerVisible: false,
      processedUids: new Set(),
      submitting: false,
      queue: [],
      uploadedUrls: [],
      configLoading: false,
      cloneUnitPrice: 0,
      agree: false,
      agreementDialogVisible: false,
      agreementLoading: false,
      agreementTitle: '',
      agreementUpdatedAt: '',
      agreementContent: '',
      agreeCountdown: 0,
      agreeTimer: null,
      form: {
        human_name: '',
        human_type: 1,
        human_scene: '客厅',
        human_video_url: ''
      },
      rules: {
        human_name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
        human_scene: [{ required: true, message: '请输入场景', trigger: 'blur' }],
        human_video_url: [{ required: true, message: '请填写视频地址', trigger: 'blur' }]
      },
      keyword: '',
      selectedIds: new Set(),
      page: 1,
      pageSize: 12,
      total: 0,
      pageSizes: [12, 24, 48, 96],
      isSearching: false,
      screenWidth: window.innerWidth
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },
    selectedIdsArr() {
      return Array.from(this.selectedIds)
    },
    isAllSelected() {
      return this.list.length > 0 && this.list.every(it => this.selectedIds.has(it.id))
    },
    videoCount() {
      return this.splitByAnyComma(this.form.human_video_url).length
    },
    totalCloneCost() {
      const total = (this.cloneUnitPrice || 0) * (this.videoCount || 0)
      return Number.isFinite(total) ? total.toFixed(2) : '0.00'
    },
    costDisplayText() {
      if (!this.videoCount) return '消耗积分'
      if (!this.cloneUnitPrice) return `预计消耗 0 点（本次免费，${this.videoCount} 个）`
      return `预计消耗 ${this.totalCloneCost} 点（${this.cloneUnitPrice} × ${this.videoCount}）`
    },
    paginationLayout() {
      if (this.screenWidth < 576) return 'prev, pager, next'
      if (this.screenWidth < 768) return 'total, prev, pager, next'
      return 'total, sizes, prev, pager, next, jumper'
    },
    dialogWidth() {
      if (this.screenWidth < 576) return '95%'
      if (this.screenWidth < 768) return '90%'
      return '720px'
    },
    formLabelWidth() {
      if (this.screenWidth < 576) return '80px'
      return '100px'
    }
  },
  mounted() {
    this.fetchCloneUnitPrice().finally(() => {
      this.loadData()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    this.clearAgreeTimer()
  },
  methods: {
    handleResize() {
      this.screenWidth = window.innerWidth
    },
    truncateUrl(url) {
      if (!url) return ''
      if (url.length > 40) {
        return url.substring(0, 20) + '...' + url.substring(url.length - 15)
      }
      return url
    },
    async loadData() {
      try {
        this.loading = true
        const body = this.isSearching
          ? {
              action: 'search',
              store_id: this.userInfo.id,
              keyword: this.keyword.trim(),
              page: this.page,
              page_size: this.pageSize
            }
          : {
              action: 'list',
              store_id: this.userInfo.id,
              page: this.page,
              page_size: this.pageSize
            }
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone.php`, body)
        this.list = data?.data || []
        this.total = data?.pagination?.total || 0
        if (!this.list.length && this.page > 1) {
          this.page = this.page - 1
          await this.loadData()
          return
        }
        this.clearSelection()
      } catch (e) {
        ElMessage.error('获取列表失败')
      } finally {
        this.loading = false
      }
    },
    onVideoMeta(item, e) {
      try {
        const video = e?.target
        const w = video?.videoWidth || 0
        const h = video?.videoHeight || 0
        if (!w || !h) {
          item._videoClass = ''
          return
        }
        const ratio = w / h
        if (ratio > 1.1) {
          item._videoClass = 'video-landscape'
        } else if (ratio < 0.9) {
          item._videoClass = 'video-portrait'
        } else {
          item._videoClass = 'video-square'
        }
      } catch (err) {
        console.warn('onVideoMeta error:', err)
        item._videoClass = ''
      }
    },
    onAgreeBoxChange(val) {
      if (val && !this.agree) {
        this.openAgreement(true)
      } else if (!val) {
        this.agree = false
      }
    },
    async openAgreement() {
      try {
        this.agreementDialogVisible = true
        this.agreementLoading = true
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_clone_agreement.php`)
        if (data?.code === 200 && data?.data) {
          this.agreementTitle = data.data.title || '克隆使用协议'
          this.agreementUpdatedAt = data.data.updated_at || ''
          this.agreementContent = data.data.content || ''
        } else {
          this.agreementTitle = '克隆使用协议'
          this.agreementUpdatedAt = ''
          this.agreementContent = ''
        }
      } catch (e) {
        ElMessage.error('读取使用协议失败')
        this.agreementContent = ''
      } finally {
        this.agreementLoading = false
        this.startAgreeCountdown(3)
      }
    },
    startAgreeCountdown(sec = 3) {
      this.clearAgreeTimer()
      this.agreeCountdown = sec
      this.agreeTimer = setInterval(() => {
        if (this.agreeCountdown > 0) {
          this.agreeCountdown--
        }
        if (this.agreeCountdown <= 0) {
          this.clearAgreeTimer()
        }
      }, 1000)
    },
    clearAgreeTimer() {
      if (this.agreeTimer) {
        clearInterval(this.agreeTimer)
        this.agreeTimer = null
      }
    },
    confirmAgree() {
      if (this.agreeCountdown > 0) return
      this.agree = true
      this.agreementDialogVisible = false
    },
    async fetchCloneUnitPrice() {
      try {
        this.configLoading = true
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_digital_human_config.php`)
        if (data?.code === 200 && data?.data?.digital_human_clone_num != null) {
          const n = parseFloat(data.data.digital_human_clone_num)
          this.cloneUnitPrice = Number.isFinite(n) ? n : 0
        } else {
          this.cloneUnitPrice = 0
        }
      } catch (err) {
        console.error('fetchCloneUnitPrice failed:', err)
        this.cloneUnitPrice = 0
      } finally {
        this.configLoading = false
      }
    },
    async onSearch() {
      this.isSearching = !!this.keyword.trim()
      this.page = 1
      await this.loadData()
    },
    async resetSearch() {
      this.keyword = ''
      this.isSearching = false
      this.page = 1
      await this.loadData()
    },
    async handlePageChange(p) {
      this.page = p
      await this.loadData()
    },
    async handleSizeChange(ps) {
      this.pageSize = ps
      this.page = 1
      await this.loadData()
    },
    clearSelection() {
      this.selectedIds.clear()
    },
    toggleOne(id) {
      if (this.selectedIds.has(id)) this.selectedIds.delete(id)
      else this.selectedIds.add(id)
    },
    toggleSelectAll() {
      if (!this.list.length) return
      if (this.isAllSelected) {
        this.selectedIds.clear()
      } else {
        this.list.forEach(it => this.selectedIds.add(it.id))
      }
    },
    async doBatchDelete() {
      if (!this.selectedIds.size) return
      try {
        this.loading = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone.php`, {
          action: 'batch_delete',
          store_id: this.userInfo.id,
          ids: Array.from(this.selectedIds)
        })
        if (data.success) {
          ElMessage.success('批量删除成功')
          await this.loadData()
        } else {
          ElMessage.error(data.error || '批量删除失败')
        }
      } catch (e) {
        ElMessage.error('批量删除失败')
      } finally {
        this.loading = false
      }
    },
    async confirmDelete(id) {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone.php`, {
          action: 'delete',
          id,
          store_id: this.userInfo.id
        })
        if (data.success) {
          ElMessage.success('已删除')
          if (this.selectedIds.has(id)) this.selectedIds.delete(id)
          await this.loadData()
        } else {
          ElMessage.error(data.error || '删除失败')
        }
      } catch (e) {
        console.info('删除失败：', e)
      }
    },
    statusName(s) {
      if (s === 2) return '失败'
      if (s === 1) return '正常'
      return '—'
    },
    insertSep(field) {
      const val = (this.form[field] ?? '').toString()
      const endsWithComma = /[，,]\s*$/.test(val)
      this.form[field] = endsWithComma ? `${val} ` : `${val}${val ? ', ' : ''}`
    },
    splitByAnyComma(str) {
      return (str || '')
        .split(/[，,]/g)
        .map(s => s.trim())
        .filter(Boolean)
    },
    openCreate() {
      this.drawerVisible = true
      this.agree = false
      this.uploadedUrls = []
      this.queue = []
      this.processedUids.clear()
      this.form = { human_name: '', human_type: 1, human_scene: '客厅', human_video_url: '' }
      this.$nextTick(() => this.$refs.formRef?.clearValidate?.())
    },
    async onPickLocalVideo(uploadFile) {
      try {
        const uid = uploadFile?.uid
        const file = uploadFile?.raw || uploadFile
        if (!file) return
        if (uid && this.processedUids.has(uid)) return
        if (uid) this.processedUids.add(uid)
        const q = {
          id: uid || `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          name: file.name || '未命名视频',
          percent: 0,
          status: undefined
        }
        this.queue.push(q)
        const ok = await beforeUpload(file, { type: 'video' })
        if (!ok) {
          q.status = 'exception'
          return
        }
        q.percent = 10
        const seconds = await getVideoDuration(file)
        if (isNaN(seconds) || seconds < 5 || seconds > 120) {
          ElMessage.error(`${q.name} 时长需在 5~120 秒，当前约 ${isNaN(seconds) ? '未知' : seconds.toFixed(1)} 秒`)
          q.status = 'exception'
          return
        }
        q.percent = 45
        const sign = await getUploadSignature('video')
        const res = await uploadToOSS(file, sign)
        q.percent = 90
        if (res?.success && res.url) {
          this.uploadedUrls.push(res.url)
          const manual = this.form.human_video_url
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
          const total = Array.from(new Set([...manual, ...this.uploadedUrls]))
          this.form.human_video_url = total.join(',')
          q.percent = 100
          q.status = 'success'
          ElMessage.success(`上传成功：${q.name}`)
        } else {
          q.status = 'exception'
          ElMessage.error(`上传失败：${q.name}`)
        }
      } catch (err) {
        console.error(err)
        ElMessage.error(err?.message || '上传失败')
      }
    },
    removeUrl(idx) {
      this.uploadedUrls.splice(idx, 1)
      const manual = this.splitByAnyComma(this.form.human_video_url)
      const remained = manual.filter(u => this.uploadedUrls.includes(u))
      this.form.human_video_url = remained.join(',')
    },
    async submit() {
      this.$refs.formRef.validate(async ok => {
        if (!ok) return
        this.submitting = true
        try {
          const urls = this.splitByAnyComma(this.form.human_video_url)
          const names = this.splitByAnyComma(this.form.human_name)
          const scenes = this.splitByAnyComma(this.form.human_scene)
          const payload = {
            action: 'add',
            store_id: this.userInfo.id,
            human_name: names.length > 1 ? names : (names[0] || ''),
            human_scene: scenes.length > 1 ? scenes : (scenes[0] || ''),
            human_type: 1,
            human_video_url: urls.length > 1 ? urls : (urls[0] || ''),
            callback_url: 'https://www.baidu.com'
          }
          const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone.php`, payload)
          if (data.success) {
            ElMessage.success('提交成功，任务已创建')
            this.drawerVisible = false
            await this.loadData()
          } else {
            ElMessage.error(data.error || '提交失败')
          }
        } catch (e) {
          ElMessage.error('提交失败')
        } finally {
          this.submitting = false
        }
      })
    },
    goCreate(row) {
      if (row.scene_task_id) {
        this.$router.push({
          path: '/human_clone/humanrecord',
          query: { cloneId: row.scene_task_id }
        })
      } else {
        ElMessage.warning('该形象尚未准备好，无法进行创作')
      }
    }
  }
}
</script>

<style scoped lang="scss">
.human-clone-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.settings-card {
  background: rgba(18, 14, 33, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #c084fc;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;

  .search-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
  }

  .search-input {
    flex: 1;
    min-width: 200px;
  }

  @media (max-width: 768px) {
    flex-direction: column;

    .search-left {
      width: 100%;
    }

    .search-left .el-input {
      width: 100% !important;
    }

    .add-btn {
      width: 100%;
    }
  }
}

.cards-wrapper {
  position: relative;
  width: 100%;
  min-height: 400px;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.data-card {
  background: rgba(26, 21, 53, 0.6);
  border-radius: 16px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(157, 78, 221, 0.5);
    box-shadow: 0 10px 30px rgba(101, 70, 180, 0.2);
  }
}

.card-checkbox {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
  background: rgba(18, 14, 33, 0.8);
  backdrop-filter: blur(4px);
  border-radius: 8px;
  padding: 4px;
}

.card-media {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #0a0a0a;
}

.video-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0f0f1a;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;

    &.video-portrait {
      object-fit: contain;
      object-position: top center;
      background: #000;
    }

    &.video-square {
      object-fit: contain;
      background: #000;
    }
  }

  .video-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5a5a7a;
  }
}

.card-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent 60%);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.25s ease;
  padding: 16px;

  .data-card:hover & {
    opacity: 1;
  }
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  &.primary-btn {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    color: #fff;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(157, 78, 221, 0.4);
    }
  }

  &.danger-btn {
    background: rgba(239, 68, 68, 0.9);
    color: #fff;
    padding: 8px 12px;

    &:hover {
      background: rgb(239, 68, 68);
    }
  }
}

.status-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);

  &.status-1 {
    color: #4ade80;
  }

  &.status-2 {
    color: #f87171;
  }

  &.status-0 {
    color: #a0a0c0;
  }
}

.card-content {
  padding: 16px;
}

.card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  .card-title {
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }

  .clone-tag {
    background: rgba(157, 78, 221, 0.2);
    color: #c084fc;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 11px;
    flex-shrink: 0;
    margin-left: 8px;
  }
}

.card-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;

  .info-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #a0a0c0;

    svg {
      color: #7a7a9a;
      flex-shrink: 0;
    }

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.card-error {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #f87171;
  border-top: 1px solid rgba(157, 78, 221, 0.15);
  padding-top: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.empty-state {
  grid-column: 1 / -1;
  padding: 60px 0;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
  }
}

// 抽屉样式
.custom-drawer {
  :deep(.el-drawer__header) {
    background: rgba(18, 14, 33, 0.98);
    border-bottom: 1px solid rgba(157, 78, 221, 0.2);
    padding: 20px 24px;
    margin-bottom: 0;

    span {
      color: #fff;
      font-weight: 600;
      font-size: 18px;
    }

    .el-drawer__close-btn {
      color: #a0a0c0;

      &:hover {
        color: #c084fc;
      }
    }
  }

  :deep(.el-drawer__body) {
    padding: 0;
    background: rgba(18, 14, 33, 0.98);
  }
}

.drawer-content {
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-section {
  background: rgba(26, 21, 53, 0.5);
  border-radius: 16px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 20px;
}

.clone-form {
  :deep(.el-form-item__label) {
    color: #a0a0c0;
    font-weight: 500;
  }

  :deep(.el-input__wrapper) {
    background: rgba(26, 21, 53, 0.8);
    border: 1px solid rgba(157, 78, 221, 0.3);
    border-radius: 10px;
    box-shadow: none;

    &:hover {
      border-color: rgba(157, 78, 221, 0.6);
    }

    &.is-focus {
      border-color: #9d4edd;
      box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
    }

    .el-input__inner {
      color: #fff;
      background: transparent;

      &::placeholder {
        color: #5a5a7a;
      }
    }
  }

  :deep(.el-textarea__inner) {
    background: rgba(26, 21, 53, 0.8);
    border: 1px solid rgba(157, 78, 221, 0.3);
    border-radius: 10px;
    color: #fff;

    &:hover {
      border-color: rgba(157, 78, 221, 0.6);
    }

    &:focus {
      border-color: #9d4edd;
      box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
    }
  }
}

.input-append-btn {
  background: rgba(157, 78, 221, 0.2);
  border: none;
  color: #c084fc;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;

  &:hover {
    background: rgba(157, 78, 221, 0.3);
  }
}

.mode-tag {
  display: inline-block;
  background: rgba(157, 78, 221, 0.2);
  color: #c084fc;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
}

.uploader-panel {
  border: 1px solid rgba(157, 78, 221, 0.2);
  border-radius: 12px;
  padding: 14px;
  background: rgba(26, 21, 53, 0.4);
}

.uploader-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.uploader-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #7a7a9a;
  font-size: 12px;
}

.uploader-price {
  color: #c084fc;
  font-size: 13px;
  margin-right: 12px;
}

.mono-input :deep(textarea) {
  font-family: monospace;
}

.url-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.url-chip {
  background: rgba(157, 78, 221, 0.15);
  border-radius: 16px;
  padding: 4px 8px 4px 12px;
  font-size: 11px;
  color: #a0a0c0;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  .chip-close {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    border-radius: 50%;
    color: #a0a0c0;

    &:hover {
      color: #f87171;
    }
  }
}

.uploader-body {
  display: grid;
  grid-template-columns: 1fr 0.9fr;
  gap: 16px;
  margin-top: 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
}

.drop-card {
  border: 1px dashed rgba(157, 78, 221, 0.4);
  border-radius: 12px;
  background: rgba(26, 21, 53, 0.6);
  transition: all 0.2s;

  &:hover {
    border-color: #9d4edd;
    background: rgba(157, 78, 221, 0.1);
  }

  :deep(.el-upload-dragger) {
    background: transparent;
    border: none;
  }
}

.drop-inner {
  padding: 24px 16px;
  text-align: center;
}

.drop-icon {
  color: #9d4edd;
  margin-bottom: 8px;
}

.drop-title {
  font-weight: 600;
  color: #fff;
  margin-bottom: 4px;
}

.drop-sub {
  font-size: 13px;
  color: #a0a0c0;

  .link {
    color: #c084fc;
    cursor: pointer;
  }
}

.drop-meta {
  font-size: 11px;
  color: #7a7a9a;
  margin-top: 8px;
}

.uploader-aside {
  background: rgba(26, 21, 53, 0.6);
  border: 1px solid rgba(157, 78, 221, 0.2);
  border-radius: 12px;
  padding: 12px 14px;
}

.aside-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 10px;
}

.aside-list {
  margin: 0;
  padding-left: 20px;
  color: #a0a0c0;
  font-size: 12px;
  line-height: 1.7;

  li {
    margin-bottom: 4px;
  }
}

.queue {
  margin-top: 12px;
  max-height: 200px;
  overflow-y: auto;

  .queue-title {
    font-weight: 600;
    color: #fff;
    margin-bottom: 8px;
    font-size: 13px;
  }

  .queue-item {
    margin-bottom: 10px;

    .q-name {
      display: block;
      font-size: 11px;
      color: #a0a0c0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-bottom: 4px;
    }
  }
}

.agreement-item {
  margin-top: 8px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: #a0a0c0;
  font-size: 13px;

  input {
    cursor: pointer;
    accent-color: #9d4edd;
  }

  .link-btn {
    background: none;
    border: none;
    color: #c084fc;
    cursor: pointer;
    padding: 0;
    font-size: 13px;

    &:hover {
      text-decoration: underline;
    }
  }
}

.form-actions {
  margin-top: 16px;
  display: flex;
  gap: 12px;
}

.submit-btn {
  background: linear-gradient(135deg, #6546b4, #9d4edd) !important;
  color: #fff !important;
}

// 指南区域
.guide-section {
  background: rgba(26, 21, 53, 0.5);
  border-radius: 16px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 20px;
}

.guide-grid {
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.sample-card {
  background: rgba(18, 14, 33, 0.6);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(157, 78, 221, 0.2);
}

.sample-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: rgba(157, 78, 221, 0.1);
  border-bottom: 1px solid rgba(157, 78, 221, 0.2);

  .sample-title {
    font-weight: 600;
    color: #fff;
  }

  svg {
    color: #c084fc;
    cursor: pointer;
  }
}

.sample-body {
  padding: 12px;
  background: #0a0a0a;

  .sample-video {
    width: 100%;
    border-radius: 8px;
    display: block;
  }
}

.tips-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tips-group {
  background: rgba(18, 14, 33, 0.6);
  border-radius: 12px;
  padding: 14px 16px;
  border: 1px solid rgba(157, 78, 221, 0.2);
}

.tips-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.tips-title {
  font-weight: 600;
  font-size: 15px;
}

.ok-tips {
  .tips-icon {
    color: #4ade80;
  }
  .tips-title {
    color: #4ade80;
  }
}

.bad-tips {
  .tips-icon {
    color: #f87171;
  }
  .tips-title {
    color: #f87171;
  }
}

.tips-list {
  margin: 0;
  padding-left: 20px;
  color: #a0a0c0;
  font-size: 12px;
  line-height: 1.8;

  b {
    color: #fff;
  }
}

// 弹窗样式
.custom-dialog {
  :deep(.el-dialog) {
    background: rgba(18, 14, 33, 0.98);
    border: 1px solid rgba(157, 78, 221, 0.3);
    border-radius: 20px;
  }

  :deep(.el-dialog__header) {
    border-bottom: 1px solid rgba(157, 78, 221, 0.2);
    padding: 20px 24px;

    .el-dialog__title {
      color: #fff;
      font-weight: 600;
    }
  }

  :deep(.el-dialog__body) {
    padding: 24px;
  }

  :deep(.el-dialog__footer) {
    border-top: 1px solid rgba(157, 78, 221, 0.2);
    padding: 16px 24px;
  }
}

.agreement-content {
  max-height: 60vh;
  overflow: auto;
}

.agreement-meta {
  color: #7a7a9a;
  font-size: 12px;
  margin-bottom: 12px;
}

.agreement-pre {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.7;
  font-size: 13px;
  color: #a0a0c0;
  font-family: inherit;
  margin: 0;
}

.dialog-btn {
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  margin-left: 12px;

  &.cancel-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #a0a0c0;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
      color: #c084fc;
    }
  }

  &.confirm-btn {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    color: #fff;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }
}

// Element Plus 组件样式覆盖
:deep(.el-pagination) {
  --el-pagination-bg-color: transparent;
  --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
  --el-pagination-hover-color: #c084fc;

  .btn-prev,
  .btn-next,
  .el-pager li {
    background: rgba(157, 78, 221, 0.1);
    border: 1px solid rgba(157, 78, 221, 0.3);
    color: #a0a0c0;
    border-radius: 8px;
    min-width: 32px;
    height: 32px;

    &:hover {
      background: rgba(157, 78, 221, 0.2);
      color: #c084fc;
    }

    &.active {
      background: linear-gradient(135deg, #6546b4, #9d4edd);
      color: #fff;
      border-color: transparent;
    }
  }

  .el-pagination__total,
  .el-pagination__jump {
    color: #a0a0c0;
  }
}

:deep(.el-progress-bar__outer) {
  background: rgba(157, 78, 221, 0.2);
}

:deep(.el-progress-bar__inner) {
  background: linear-gradient(90deg, #6546b4, #9d4edd);
}

:deep(.el-checkbox__inner) {
  background: rgba(26, 21, 53, 0.8);
  border-color: rgba(157, 78, 221, 0.4);

  &:hover {
    border-color: #9d4edd;
  }
}

:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background: #9d4edd;
  border-color: #9d4edd;
}

:deep(.el-empty__description p) {
  color: #a0a0c0;
}

@media (max-width: 576px) {
  .btn-text {
    display: none;
  }

  .action-btn {
    padding: 6px 10px;
  }
}
</style>